import React from 'react';

interface BaseProps {
  label?: string;
  description?: string;
  error?: string;
  className?: string;
  id?: string;
}

interface InputFieldProps extends BaseProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  multiline?: undefined;
}

interface TextareaFieldProps extends BaseProps, Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  multiline: true;
  rows?: number;
}

export type FieldProps = InputFieldProps | TextareaFieldProps;

let nextId = 0;
const useFieldId = (provided?: string) => {
  const ref = React.useRef<string | null>(null);
  if (!ref.current) {
    ref.current = provided ?? `rr-field-${++nextId}`;
  }
  return ref.current;
};

const Field: React.FC<FieldProps> = (props) => {
  const id = useFieldId(props.id);
  const wrap = (input: React.ReactNode) => (
    <div className={['rr-field-wrap', props.className].filter(Boolean).join(' ')}>
      {props.label && (
        <label htmlFor={id} className="rr-field-label">
          {props.label}
        </label>
      )}
      {props.description && <div className="rr-field-description">{props.description}</div>}
      {input}
      {props.error && <div className="rr-field-error-msg">{props.error}</div>}
    </div>
  );

  if ('multiline' in props && props.multiline) {
    const { label, description, error, className, multiline, ...rest } = props;
    return wrap(
      <textarea
        id={id}
        className={['rr-field', error ? 'rr-field-error' : ''].filter(Boolean).join(' ')}
        {...rest}
      />
    );
  }

  const { label, description, error, className, ...rest } = props as InputFieldProps;
  return wrap(
    <input
      id={id}
      className={['rr-field', error ? 'rr-field-error' : ''].filter(Boolean).join(' ')}
      {...rest}
    />
  );
};

export default Field;
